import { useState, type FormEvent } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Link, Stack } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { api, ApiError } from '@/api';
import { PasswordField } from '@/components/form/PasswordField';
import { AuthHeader } from './AuthHeader';
import { FormError } from './FormError';

/** Opened from the e-mailed link: /reset-password?token=... */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const passwordError = touched && password.length < 8 ? 'Use pelo menos 8 caracteres.' : undefined;
  const confirmError = touched && confirm !== password ? 'As senhas não conferem.' : undefined;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (password.length < 8 || confirm !== password) return;
    setPending(true);
    setError(null);
    try {
      await api.post('/auth/password-reset/confirm', { token, password }, { auth: false });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível redefinir a senha.');
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <AuthHeader title="Nova senha" subtitle="Escolha uma senha para entrar na sua conta." />
      <FormError message={error} />
      {!token ? (
        <Alert severity="error">Link inválido. Peça um novo em “Esqueci minha senha”.</Alert>
      ) : done ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Senha alterada!</AlertTitle>
          Agora é só entrar com a nova senha.
        </Alert>
      ) : (
        <Box component="form" noValidate onSubmit={submit}>
          <Stack spacing={2.5}>
            <PasswordField
              label="Nova senha"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(passwordError)}
              helperText={passwordError}
            />
            <PasswordField
              label="Confirme a senha"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={Boolean(confirmError)}
              helperText={confirmError}
            />
            <Button type="submit" variant="contained" size="large" fullWidth disabled={pending}>
              {pending ? <CircularProgress size={22} color="inherit" /> : 'Salvar nova senha'}
            </Button>
          </Stack>
        </Box>
      )}
      <Link
        component={RouterLink}
        to="/login"
        variant="body2"
        sx={{ display: 'block', textAlign: 'center', mt: 3 }}
      >
        Voltar para o login
      </Link>
    </>
  );
}
